'use client'

import type { GroundData } from '@/app/_components/ground/load-ground-data'
import { SpecimenFrame } from '@/app/_components/ground/specimen-frame'
import {
    DRAW_CANVAS_BOX,
    DRAW_CANVAS_SVG,
    DRAW_VERDICT_CLASS,
    DRAW_VERDICTS,
} from '@/components/practice/draw-canvas'
import { strokeClock } from '@/features/landing/stroke/stroke-clock'
import { StrokeDraw } from '@/features/landing/stroke/stroke-draw'
import { DrawPrompt } from '@/learn/kanji/[frame]/_components/draw-prompt'
import { MarginaliaTop } from '@/learn/kanji/[frame]/_components/marginalia-top'
import { StrokeAffordance } from '@/learn/kanji/[frame]/_components/stroke-affordance'
import { cn } from '@/lib/utils'

// Beats 02–03: the draw beat for frame 15 (目). The app's DrawCanvas owns
// its strokes and verdict in state with no way to preset them, so the
// specimen renders the app's own prompt, box, surface and verdict line
// (shared leaf and constants) with the canonical strokes drawn by the scroll: stroke i
// draws over t ∈ [i/n, (i+1)/n]. The verdict beat holds every stroke
// drawn and shows the app's accepted line.
//
// The app centres this beat in the viewport; the specimen top-aligns it
// (and tightens the app's gap-8 to gap-6) because page 2 rises over the
// ground from the bottom for the whole of gap 1: centred, the canvas's
// last strokes and the verdict line drew under the sheet (§7.2).
export function DrawSpecimen({
    data,
    t,
    verdict,
}: {
    data: Pick<GroundData, 'lesson' | 'eye' | 'eyeStrokes'>
    t: number
    verdict: boolean
}) {
    const k = data.eye
    const paths = data.eyeStrokes.strokePaths
    const n = paths.length
    const drawn = verdict ? paths.map(() => 1) : strokeClock(t * n, n)
    return (
        <SpecimenFrame beat={verdict ? 3 : 2}>
            <MarginaliaTop
                lessonNumber={data.lesson.number}
                frame={Number(k.frameNumber)}
            />
            {/* Below xl, where the caption docks, beat 02's focus is the
                keyword with the square (rule 4: the strip never covers the
                keyword on the draw beat); beat 03's is the square with its
                verdict, so the camera pans to the verdict as it appears.
                From xl nothing docks and the square with its verdict is
                the focus for both. */}
            <div
                className="flex flex-1 flex-col items-center gap-6 pt-2"
                data-focus-docked={verdict ? undefined : ''}
            >
                <div className="flex w-full max-w-md flex-col gap-5">
                    <DrawPrompt keyword={k.meaning} />
                    <StrokeAffordance
                        character={k.character}
                        strokeCount={k.strokeCount}
                        revealStages={k.revealStages}
                        gradeSlice={k.gradeSlice}
                        strokeData={data.eyeStrokes}
                        strokeHost={k.strokeHost}
                        centered
                    />
                </div>
                <div className="flex flex-col items-center gap-6" data-focus>
                    <div className={cn('relative', DRAW_CANVAS_BOX)}>
                        <StrokeDraw
                            strokePaths={paths}
                            viewBox={data.eyeStrokes.viewBox}
                            drawn={drawn}
                            strokeWidth={3}
                            className={cn(DRAW_CANVAS_SVG, 'absolute inset-0')}
                            data-ink
                        />
                    </div>
                    <div aria-live="off" className="min-h-6">
                        {verdict ? (
                            <p className={DRAW_VERDICT_CLASS}>
                                {DRAW_VERDICTS.accepted}
                            </p>
                        ) : null}
                    </div>
                </div>
            </div>
        </SpecimenFrame>
    )
}
